// ABOUTME: React context for global portfolio state management
// ABOUTME: Active portfolio is per-user server preference (defaultPortfolioId from /me/preferences) with one-time legacy localStorage migration

import type { MePreferences } from '@alpha-finder/contracts/me-preferences';
import type { EnrichedPortfolio, Portfolio } from '@alpha-finder/contracts/portfolio';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { PREFERENCES_QUERY_KEY, usePreferences } from '../hooks/usePreferences';
import { meApi } from '../lib/api/me';
import {
  invalidatePortfolioData,
  portfolioEnrichedQueryOptions,
  portfoliosQueryOptions,
} from '../lib/queries/portfolios';

interface PortfolioContextValue {
  portfolios: Portfolio[];
  activePortfolioId: string | null;
  activePortfolio: EnrichedPortfolio | null;
  isLoading: boolean;
  error: Error | null;
  /** Explicit selection: changes the active portfolio AND writes the server-side default. Used by the sidebar selector and Create-portfolio success path. */
  setActivePortfolio: (id: string) => void;
  /** URL-driven selection: updates the active portfolio locally only. Use when navigation should NOT change the user's stored default (e.g. /portfolios/:id route sync). */
  setActivePortfolioId: (id: string) => void;
  refetch: () => Promise<void>;
}

const PortfolioContext = createContext<PortfolioContextValue | null>(null);

const LEGACY_ACTIVE_PORTFOLIO_KEY = 'alpha-finder-active-portfolio';

function portfolioIdInPath(pathname: string): string | null {
  return (
    matchPath('/portfolios/:id', pathname)?.params.id ??
    matchPath('/portfolio/:id', pathname)?.params.id ??
    null
  );
}

export function PortfolioProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const {
    defaultPortfolioId,
    isLoading: isLoadingPreferences,
    isError: preferencesFailed,
  } = usePreferences();
  const [selectedPortfolioId, setActivePortfolioIdState] = useState<string | null>(null);
  const { pathname } = useLocation();

  // Fetch all portfolios
  const {
    data: portfoliosData,
    isLoading: isLoadingList,
    error: listError,
    refetch: refetchList,
  } = useQuery(portfoliosQueryOptions);

  const portfolios = useMemo(() => portfoliosData || [], [portfoliosData]);

  const pathPortfolioId = portfolioIdInPath(pathname);
  const routePortfolioId =
    pathPortfolioId !== null && portfolios.some((p) => p.id === pathPortfolioId)
      ? pathPortfolioId
      : null;
  const activePortfolioId = routePortfolioId ?? selectedPortfolioId;

  useEffect(() => {
    if (routePortfolioId) setActivePortfolioIdState(routePortfolioId);
  }, [routePortfolioId]);

  // Fetch active portfolio enriched payload (positions + currency + fx + tags + availableTags)
  const {
    data: activePortfolioData,
    isLoading: isLoadingActive,
    error: activeError,
  } = useQuery(portfolioEnrichedQueryOptions(activePortfolioId));

  const persistDefaultPortfolio = useCallback(
    async (id: string) => {
      await meApi.setDefaultPortfolio(id);
      queryClient.setQueryData<MePreferences>(
        PREFERENCES_QUERY_KEY,
        (prefs) => prefs && { ...prefs, defaultPortfolioId: id }
      );
    },
    [queryClient]
  );

  // Selection precedence:
  //   0. a known portfolio id in the URL (local only; applied during render)
  //   1. defaultPortfolioId (server-side preference; selection waits until it has loaded)
  //   2. legacy localStorage value — migrated to the server once, then dropped (not while the
  //      preferences failed to load: nothing is persisted then)
  //   3. first portfolio in the user's list
  //
  // One-shot guard: once a valid active portfolio is selected, the effect no
  // longer fires on activePortfolioId changes — preventing the snap-back when
  // the user picks a non-default and the cached preferences are stale.
  // biome-ignore lint/correctness/useExhaustiveDependencies: activePortfolioId is read but intentionally not a trigger; including it would snap user picks back to defaultPortfolioId while the cached preferences are stale.
  useEffect(() => {
    if (portfolios.length === 0 || routePortfolioId || isLoadingPreferences) return;

    const exists = (id: string | null): boolean =>
      id !== null && portfolios.some((p) => p.id === id);

    if (activePortfolioId && exists(activePortfolioId)) return;

    if (exists(defaultPortfolioId)) {
      setActivePortfolioIdState(defaultPortfolioId);
      return;
    }

    let legacy: string | null = null;
    try {
      legacy = preferencesFailed ? null : localStorage.getItem(LEGACY_ACTIVE_PORTFOLIO_KEY);
    } catch {
      // storage unavailable; treat as unset
    }
    if (exists(legacy)) {
      const legacyId = legacy as string;
      setActivePortfolioIdState(legacyId);
      void persistDefaultPortfolio(legacyId).catch(() => {
        // migration is best-effort; UI selection already moved
      });
      try {
        localStorage.removeItem(LEGACY_ACTIVE_PORTFOLIO_KEY);
      } catch {
        // ignore
      }
      return;
    }

    setActivePortfolioIdState(portfolios[0].id);
  }, [
    portfolios,
    defaultPortfolioId,
    routePortfolioId,
    isLoadingPreferences,
    preferencesFailed,
    persistDefaultPortfolio,
  ]);

  const setActivePortfolioId = useCallback((id: string) => {
    setActivePortfolioIdState(id);
  }, []);

  const setActivePortfolio = useCallback(
    (id: string) => {
      setActivePortfolioIdState(id);
      if (preferencesFailed) return;
      void persistDefaultPortfolio(id).catch(() => {
        // server update failed; local UI selection persists for this session
      });
    },
    [preferencesFailed, persistDefaultPortfolio]
  );

  const refetch = useCallback(async () => {
    await refetchList();
    if (activePortfolioId) {
      await invalidatePortfolioData(queryClient, activePortfolioId);
    }
  }, [refetchList, activePortfolioId, queryClient]);

  const isLoading = isLoadingList || isLoadingActive;
  const error = listError || activeError;
  const value = useMemo<PortfolioContextValue>(
    () => ({
      portfolios,
      activePortfolioId,
      activePortfolio: activePortfolioData || null,
      isLoading,
      error,
      setActivePortfolio,
      setActivePortfolioId,
      refetch,
    }),
    [
      portfolios,
      activePortfolioId,
      activePortfolioData,
      isLoading,
      error,
      setActivePortfolio,
      setActivePortfolioId,
      refetch,
    ]
  );

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

export function usePortfolios() {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolios must be used within a PortfolioProvider');
  }
  return context;
}

export function useActivePortfolio() {
  const { activePortfolio, isLoading, error } = usePortfolios();
  return { portfolio: activePortfolio, isLoading, error };
}

export function usePortfolioSymbols(portfolioId?: string) {
  const { activePortfolio, activePortfolioId } = usePortfolios();

  // Fetch specific portfolio (enriched) if different from active
  const { data: specificPortfolio } = useQuery({
    ...portfolioEnrichedQueryOptions(portfolioId ?? null),
    enabled: !!portfolioId && portfolioId !== activePortfolioId,
    staleTime: 5 * 60 * 1000,
  });

  // If a specific portfolioId is requested
  if (portfolioId) {
    // Use active portfolio if it matches
    if (portfolioId === activePortfolioId && activePortfolio) {
      return activePortfolio.positions?.map((p) => p.symbol) || [];
    }
    // Use fetched specific portfolio
    return specificPortfolio?.positions?.map((p) => p.symbol) || [];
  }

  // Default: return active portfolio symbols
  return activePortfolio?.positions?.map((p) => p.symbol) || [];
}
